/**
 * useInviteValidation
 *
 * TanStack Query mutation hook for validating invite codes
 *
 * API: POST /api/v1/credentialing/invites/validate
 *
 * Request body:
 * - code: string
 *
 * Response:
 * - valid: boolean
 * - invite?: { county, tier, expiresAt }
 * - error?: 'INVALID_CODE' | 'EXPIRED' | 'ALREADY_USED' | 'REVOKED'
 */

import { useMutation } from '@tanstack/react-query';
import type { InviteValidationResult } from '../types';

export function useInviteValidation() {
  return useMutation({
    mutationFn: async (code: string): Promise<InviteValidationResult> => {
      // Note: This endpoint is being built in the backend
      // For now, we'll use fetch directly until the OpenAPI spec is updated
      const response = await fetch('/credentialing/invites/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          valid: false,
          error: data.error as 'INVALID_CODE' | 'EXPIRED' | 'ALREADY_USED' | 'REVOKED',
        };
      }

      return data as InviteValidationResult;
    },
  });
}
